import { useState, useEffect, type Dispatch, type SetStateAction } from "react";

import { api } from "~/api";
import type { CustomerSearchResult } from "~/types";

interface UseCustomerSearchResult {
  results: CustomerSearchResult[];
  loading: boolean;
  highlightedIndex: number;
  setHighlightedIndex: Dispatch<SetStateAction<number>>;
}

/**
 * Debounced customer search, shared by CustomerPicker and
 * CustomerSearchModal. `enabled` lets each caller apply its own "don't search
 * yet" rule (a combobox with something already selected, a too-short query).
 */
export function useCustomerSearch(
  query: string,
  enabled: boolean,
): UseCustomerSearchResult {
  const [results, setResults] = useState<CustomerSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  useEffect(() => {
    if (!enabled) {
      setResults([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    const debounceTimer = setTimeout(async () => {
      try {
        const data = await api.searchCustomers(query || undefined);
        if (cancelled) return;
        setResults(data);
        setHighlightedIndex(0);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to search customers:", error);
        setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 150);

    return () => {
      cancelled = true;
      clearTimeout(debounceTimer);
    };
  }, [query, enabled]);

  return { results, loading, highlightedIndex, setHighlightedIndex };
}
